import { env } from '@/env';

import pkg from '../../package.json' with { type: 'json' };

const apiVersions = {
    v2: '/v2',
    v3: '/v3',
} as const;

const apiBase = `${env.VITE_API}${apiVersions.v2}`;

// SaaS instances serve the public API from a shared host, not from the Cloud Run URL the app itself talks to.
const publicApiBase = env.VITE_SAAS ? `https://api.passwd.team/api${apiVersions.v2}` : apiBase;

export const config = {
    dev: {
        reduxDevTools: !env.VITE_PROD,
        felaDevMode: !env.VITE_PROD,
        sentryDebug: !env.VITE_PROD,
        logToConsole: !env.VITE_PROD,
    },
    api: {
        base: apiBase,
        v3base: `${env.VITE_API}${apiVersions.v3}`,
        root: '/',
        oauthCallback: '/oauth/callback',
        oauthRefreshToken: '/oauth/refresh-token',
        me: '/me',
        secrets: '/secrets',
        secret: '/secrets/:id',
        totp: '/secrets/:id/totp',
        ignoreReport: '/secrets/:id/reportDismiss',
        share: '/secrets/:id/share',
        sharedSecret: '/share/:id',
        sharedSecretTotp: '/share/:id/totp',
        groupsQuery: '/groups-query',
        groupMembers: '/groups/:id/members',
        hideGroup: '/groups/:id/hide',
        showGroup: '/groups/:id/show',
        contactsQuery: '/contacts-query',
        demoUsers: '/sandbox-contacts',
        export: '/secret-exports',
        importValidation: '/secret-validations',
        vaultImportValidation: '/vaults/:vaultId/secret-validations',
        securityAudit: '/password-reports',
        feedback: '/feedback',
        settings: '/settings',
        settingsAdmin: '/settings/admin',
        tag: '/tags/:tag',
        saasCheckout: '/saas/checkout',
        iapRedirect: '/iap-redirect',
    },
    publicApi: {
        baseUrl: publicApiBase,
    },
    googleOAuth: {
        origin: 'https://accounts.google.com/o/oauth2/v2/auth',
        clientId: env.VITE_CLIENT_ID,
        redirectUrl: '/oauth/redirect',
        signInRedirectUrl: '/oauth/sign-in',
    },
    googleTagManager: {
        trackingCode: 'GTM-NVV37XS',
    },
    sentry: {
        dsn: 'https://d6779e9ca5af48b8b5f27fe633e2932d@o4503936302120960.ingest.sentry.io/4504197311037440',
    },
    routes: {
        home: '/',
        logout: '/logout',
        existingInstallation: '/custom-url',
        secrets: '/secrets',
        vault: '/vaults/:vaultId',
        newSecret: '/secrets/new',
        secret: '/secrets/:id',
        share: '/share/:id',
        updateSecret: '/secrets/:id/update',
        importExport: '/import-export',
        securityAudit: '/security-audit',
        securityAuditUpdateSecret: '/security-audit/:id',
        securityAuditLevel: '/security-audit-level',
        securityAuditLevelUpdateSecret: '/security-audit-level/:id',
        securityAuditAge: '/security-audit-age',
        securityAuditAgeUpdateSecret: '/security-audit-age/:id',
        securityAuditLeaks: '/security-audit-leaks',
        securityAuditLeaksUpdateSecret: '/security-audit-leaks/:id',
        changelog: '/changelog',
        settings: '/settings',
        tagsSettings: '/settings/tags',
        vaultsSettings: '/settings/vaults',
        groupsVisibilitySettings: '/settings/groups-visibility',
        userAdministration: '/user-administration',
        apiKeys: '/api-keys',
    },
    links: {
        emergency: 'https://passwd.team/blog/emergency-troubleshooting-guide/',
        roles: 'https://passwd.team/docs/features/roles-and-whitelist/',
        faq: 'https://passwd.team/en/faq/',
        docs: 'https://passwd.team/docs/',
        guide: 'https://passwd.team/blog/advanced-features/',
        whatIsNew: 'https://passwd.team/blog/tag/whats-new/',
        leaks: 'https://passwd.team/blog/password-security-report-and-leaks/',
        import: 'https://passwd.team/docs/features/export-and-import/records-import/',
        freeTierUpgrade: 'https://passwd.team/docs/installation/upgrade-from-free-tier/',
        freeTierRegistration: 'https://passwd.team/en/payment/free/',
        payment: 'https://passwd.team/payment/',
        stripe: env.VITE_STRIPE_LINK,
        workspace: 'https://admin.google.com/',
        googleWorkspaceMarketplace: 'https://workspace.google.com/marketplace/app/passwd_password_manager_for_google_works/820025595231',
        docsPasskeys: 'https://passwd.team/docs/features/passkeys',
        docsPublicApi: 'https://passwd.team/docs/features/public-api/',
        sitesWithPasskeys: 'https://passwd.team/docs/features/passkeys/services-supporting-passkeys/',
        privacyPolicy: 'https://passwd.team/en/privacy-policy/',
        googlePlay: 'https://play.google.com/store/apps/details?id=team.passwd.manager&hl=en',
        appStore: 'https://apps.apple.com/cz/app/passwd-team-password-manager/id6751105030',
        roadmap: 'https://trello.com/b/J6HWVTFZ/passwd-product-roadmap',
        reviewPrizepoolTermsAndConditions: 'https://docs.google.com/document/d/1ii2xTPmdz0swo1E9YFpDlKr5JHEDyMurA08YdfYxehA',
    },
    embeds: {
        pricing: env.VITE_PROD
            ? 'https://passwd.team/{locale}/embed/pricing/'
            : 'https://microsite-development.passwd.team/{locale}/embed/pricing/',
    },
    demo: {
        enable: env.VITE_DEMO,
        gdpr: 'https://passwd.team/en/privacy-policy/',
        buyPageUrl: 'https://passwd.team/#pricing',
    },
    formsparkLinks: {
        reviewPrizepool: 'https://submit-form.com/S0FeZhMLW',
    },
    socialIntentsAppId: '2c9fab3f9df0a5b8019dfda8d373108f',
    serviceWorker: !env.VITE_DEMO && !env.VITE_PLAYWRIGHT_RUN && env.VITE_SERVICE_WORKER,
    publicChangelog: `/CHANGELOG.md?version=${pkg.version}`,
    version: pkg.version,
    isLocalhost: env.VITE_LOCALHOST,
} as const;
